import React from 'react';
import styled from 'styled-components';
import { registry } from '../../core/Registry.js';

const Box = styled.div`
  width: ${props => props.width || '100%'};
  max-width: 350px;
  background: var(--glass-1);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius, 16px);
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 30px rgba(0,0,0,0.2), 0 0 15px rgba(6,182,212,0.1);
    border-color: var(--glass-border-hover);
  }
`;

const ImgWrapper = styled.div`
  width: 100%;
  height: 220px;
  background: rgba(255,255,255,0.02);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Img = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const Content = styled.div`
  padding: 20px;
  text-align: ${props => props.align || 'left'};
`;

const Category = styled.div`
  font-size: 11px;
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 6px;
  font-weight: 700;
`;

const Title = styled.h3`
  margin: 0 0 10px 0;
  color: var(--text-primary);
  font-size: 18px;
  line-height: 1.3;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: ${props => props.align === 'center' ? 'center' : (props.align === 'right' ? 'flex-end' : 'flex-start')};
  gap: 8px;
  margin-bottom: 20px;
`;

const Price = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: var(--text-primary);
`;

const OldPrice = styled.div`
  font-size: 14px;
  color: var(--text-muted);
  text-decoration: line-through;
  margin-bottom: 4px;
`;

const AddBtn = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  background: var(--glass-2);
  border: 1px solid var(--glass-border);
  color: var(--text-primary);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(135deg, var(--accent), var(--accent-neon));
    border-color: var(--accent);
    color: #fff;
    box-shadow: 0 4px 15px var(--accent-glow);
  }
  
  svg { width: 16px; height: 16px; }
`;

const Badge = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background: var(--danger, #ef4444);
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(239, 68, 68, 0.4);
`;

const ProductBoxWidget = ({ node }) => {
  const content = node.content || {};
  const style = node.style?.desktop || {};

  return (
    <Box width={style.width}>
      <div style={{position: 'relative'}}>
        {content.badge && <Badge>{content.badge}</Badge>}
        <ImgWrapper>
          <Img src={content.image || 'https://via.placeholder.com/300x300/1e293b/00A9E0?text=Produkt'} alt={content.title} />
        </ImgWrapper>
      </div>
      <Content align={style.align}>
        {content.category && <Category>{content.category}</Category>}
        <Title>{content.title || 'Produkt Name'}</Title>
        <PriceRow align={style.align}>
          <Price>{content.price || '€99.00'}</Price>
          {content.oldPrice && <OldPrice>{content.oldPrice}</OldPrice>}
        </PriceRow>
        <AddBtn className="tef-bubbles">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
          {content.btnText || 'In den Warenkorb'}
        </AddBtn>
      </Content>
    </Box>
  );
};

registry.register('product_box', {
  label: 'Produkt Kachel',
  icon: '🛍️',
  category: 'ecommerce',
  defaultContent: {
    category: 'Smartphones',
    title: 'iPhone 15 Pro Max',
    price: '€1,199.00',
    oldPrice: '€1,299.00',
    badge: 'Sale',
    btnText: 'In den Warenkorb',
    image: 'https://via.placeholder.com/300x300/1e293b/00A9E0?text=iPhone'
  },
  defaultStyle: {
    desktop: { width: '100%', align: 'left' }
  },
  render: ProductBoxWidget,
  inspectorControls: [
    {
      tab: 'content',
      groups: [
        {
          title: 'Produktdaten',
          fields: [
            { name: 'content.image', label: 'Bild URL', type: 'text' },
            { name: 'content.title', label: 'Titel', type: 'text' },
            { name: 'content.category', label: 'Kategorie', type: 'text' },
            { name: 'content.price', label: 'Aktueller Preis', type: 'text' },
            { name: 'content.oldPrice', label: 'Alter Preis (optional)', type: 'text' },
            { name: 'content.badge', label: 'Badge Text (z.B. Sale)', type: 'text' },
            { name: 'content.btnText', label: 'Button', type: 'text' }
          ]
        }
      ]
    }, {
      tab: 'style',
      groups: [
        { title: 'Layout', fields: [ {name:'style.desktop.align', label:'Text Ausrichtung', type:'select', options:[{label:'Links',value:'left'},{label:'Zentriert',value:'center'}]}] }
      ]
    }
  ]
});
